import { z } from "zod";

import { phoneNumberSchema } from "@/shared/utils/phone-utils";

export enum MessageRecoveryStatus {
  AlreadySaved = "already_saved",
  Failed = "failed",
  NotEligible = "not_eligible",
  Saved = "saved",
  UnmatchedPatient = "unmatched_patient",
  UnresolvedInput = "unresolved_input",
  WriteUncertain = "write_uncertain",
}

export enum MessageRecoveryReason {
  CapabilityDisabled = "capability_disabled",
  DeadlineExceeded = "deadline_exceeded",
  MessageMismatch = "message_mismatch",
  MissingDraft = "missing_draft",
  NoMessageWorkflow = "no_message_workflow",
  PendingCorrection = "pending_correction",
  PendingTransfer = "pending_transfer",
  RefillWorkflow = "refill_workflow",
  SubmissionFailed = "submission_failed",
  SubmissionRejected = "submission_rejected",
  UnmatchedPatient = "unmatched_patient",
  UnresolvedCallerInput = "unresolved_caller_input",
  WorkflowBusy = "workflow_busy",
}

export const messageRecoveryOutcomeSchema = z.object({
  status: z.nativeEnum(MessageRecoveryStatus),
  reason: z.nativeEnum(MessageRecoveryReason).optional(),
  hasCapturedMessage: z.boolean(),
  messageIntakeStarted: z.boolean().optional(),
  patientMatched: z.boolean(),
  submissionAttempted: z.boolean(),
  externalId: z.string().min(1).optional(),
});

export type MessageRecoveryOutcome = z.infer<typeof messageRecoveryOutcomeSchema>;

/** Identifies interrupted message intake requiring human review of the saved call. */
export function requiresMessageRecoveryReview(outcome: MessageRecoveryOutcome | undefined): boolean {
  if (!outcome || (!outcome.hasCapturedMessage && !outcome.messageIntakeStarted)) return false;

  return (
    outcome.status === MessageRecoveryStatus.Failed ||
    outcome.status === MessageRecoveryStatus.UnmatchedPatient ||
    outcome.status === MessageRecoveryStatus.UnresolvedInput ||
    outcome.status === MessageRecoveryStatus.WriteUncertain ||
    (outcome.status === MessageRecoveryStatus.NotEligible &&
      (outcome.reason === MessageRecoveryReason.PendingCorrection ||
        outcome.reason === MessageRecoveryReason.MissingDraft ||
        outcome.reason === MessageRecoveryReason.SubmissionRejected))
  );
}

export const messageRecoveryFailureSchema = z.object({
  calledNumber: phoneNumberSchema,
  livekitRoomId: z.string().min(1).max(200),
  submissionAttempted: z.boolean(),
});

export type MessageRecoveryFailure = z.infer<typeof messageRecoveryFailureSchema>;

export const messageRecoveryFailureResponseSchema = z.object({ success: z.literal(true) });
